import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  EmailOAuthAccount,
  GetEmailLabelRulesResponse,
  ToggleEmailLabellingResponse,
  EmailLabelRule,
} from '@dreamai/types';
import api from '../../../api';
import { AppTab } from './appLayoutConfig';
import EmailTagRules from '../email/EmailTagRules';
import './inbound-email-wrapper.scss';

interface InboundEmailWrapperProps {
  threadId?: string;
  tabId?: string;
  onTabsReady?: (tabs: AppTab[]) => void;
}

const INBOUND_EMAIL_TABS: AppTab[] = [
  { id: 'email-tagging', label: 'Email Tagging' },
];

const InboundEmailWrapper: React.FC<InboundEmailWrapperProps> = ({
  threadId,
  tabId = 'email-tagging',
  onTabsReady,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [rules, setRules] = useState<EmailLabelRule[]>([]);
  const [accounts, setAccounts] = useState<EmailOAuthAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingOauthId, setTogglingOauthId] = useState<number | null>(null);
  const lastTabsRef = useRef<string>('');

  const fetchRules = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetEmailLabelRulesResponse>(
        '/email-label-rules',
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        setRules(response.data.rules);
        setAccounts(response.data.accounts);
      } else {
        setError('Failed to load email label rules');
      }
    } catch (err) {
      console.error('Error fetching email label rules:', err);
      setError('Failed to load email label rules');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  // Notify parent of tabs
  useEffect(() => {
    if (loading || !onTabsReady) return;

    const tabs = INBOUND_EMAIL_TABS;
    const tabsKey = JSON.stringify(tabs);

    if (tabsKey !== lastTabsRef.current) {
      lastTabsRef.current = tabsKey;
      onTabsReady(tabs);
    }
  }, [loading, onTabsReady]);

  const handleToggleLabelling = async (account: EmailOAuthAccount) => {
    if (togglingOauthId !== null) return;
    setTogglingOauthId(account.oauthId);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.patch<ToggleEmailLabellingResponse>(
        '/email-label-rules/toggle-labelling',
        { oauthId: account.oauthId, enabled: !account.emailLabelling },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        setAccounts((prev) =>
          prev.map((a) =>
            a.oauthId === response.data.oauthId
              ? { ...a, emailLabelling: response.data.emailLabellingEnabled }
              : a,
          ),
        );
      }
    } catch (err) {
      console.error('Error toggling email labelling:', err);
    } finally {
      setTogglingOauthId(null);
    }
  };

  if (loading) {
    return <div className='inbound-email-wrapper loading'></div>;
  }

  if (error) {
    return null;
  }

  return (
    <div className='inbound-email-wrapper'>
      <div className='email-tagging-header'>
        <div className='header-text'>
          <h1>Email tagging</h1>
          <p>Your secretary will keep your inbox tidy</p>
        </div>
      </div>

      {accounts.length > 0 && (
        <div className='labelling-toggles'>
          {accounts.map((account) => (
            <div key={account.oauthId} className='labelling-toggle'>
              <div className='toggle-info'>
                <span className='toggle-label'>
                  Automatically tag incoming emails to
                </span>
                <span className='toggle-email'>{account.email}</span>
              </div>
              <button
                className={`toggle-switch ${account.emailLabelling ? 'on' : 'off'}`}
                onClick={() => handleToggleLabelling(account)}
                disabled={togglingOauthId !== null}
                aria-label={`Toggle email labelling for ${account.email}`}
              >
                <span className='toggle-thumb' />
              </button>
            </div>
          ))}
        </div>
      )}

      <EmailTagRules
        rules={rules}
        onRulesChange={setRules}
        showHeader={false}
      />
    </div>
  );
};

export default InboundEmailWrapper;
